import React, { useState, useRef, useEffect } from 'react';
import moment from 'moment';
import OrderSummary from '../../dashboard/components/order-summary';
import PassbookService from '../../../services/passbook.service';
import Loader from '../../../shared/common/loader';
import { formatAmount } from '../../../utils';
import { userStore } from 'store/UserStore';
import '../table.css';

interface PropsType {
  metalData: any;
  type: string;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  COMPLETED: { bg: '#E6F4EC', color: '#1A6637' },
  PENDING: { bg: '#FEF6E0', color: '#7A5010' },
  FAILED: { bg: '#FDEAEA', color: '#961B1B' },
  CANCELLED: { bg: '#F1F2F4', color: '#5C6470' },
  PROCESSING: { bg: '#E6EEFF', color: '#1E3A8A' },
};
const getStatusStyle = (status: string) =>
  STATUS_COLORS[status] ?? { bg: '#F1F2F4', color: '#5C6470' };

const getPassbookAction = (
  paymentMethod: any,
  orderCustid: any,
  metaltyp: any,
  customerId: any
): { label: string; verb: 'buy' | 'sell' | 'gift' } => {
  switch (paymentMethod) {
    case 'GIFT':
    case 'CORP_GIFT':
      return orderCustid === customerId
        ? { label: 'Gifted', verb: 'gift' }
        : { label: 'Gift Received', verb: 'gift' };
    default:
      return metaltyp === 'SELL'
        ? { label: 'Bought', verb: 'buy' }
        : { label: 'Sold', verb: 'sell' };
  }
};

const getDiscountText = (
  discType: any,
  value: any,
  secondaryMetal: any,
  primaryMetal: any,
  cashbackType?: any
): string => {
  switch (discType) {
    case 'PRODUCT_QTY':
      return `${value}g ${secondaryMetal ?? primaryMetal} free`;
    case 'AMOUNT':
    case 'RATE':
      return cashbackType === 'CASHBACK'
        ? `₹${parseFloat(value).toFixed(2)} cashback`
        : `₹${parseFloat(value).toFixed(2)} off`;
    default:
      return '';
  }
};

const EbRef = ({ value }: { value: string }) => {
  const match = value?.match(/^(eb-r-)(.*)/i);
  if (match) {
    return <>
      <span className="eb-prefix">{match[1]}</span>{match[2]}
    </>;
  }
  return <>{value}</>;
};

const TypeBadge = ({ metal }: { metal: any }) => {
  const pm = metal.order?.paymentMethod;
  if (pm === 'GIFT' || pm === 'CORP_GIFT') return <span className="txn-type-badge gift">GIFT</span>;
  if (metal.type === 'SELL') return <span className="txn-type-badge buy">BUY</span>;
  return <span className="txn-type-badge sell">SELL</span>;
};

const StatusBadge = ({ status }: { status: string }) => {
  const sc = getStatusStyle(status);
  return (
    <span className="txn-status-badge" style={{ background: sc.bg, color: sc.color }}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};


const OrderCell = ({ metal, customerId }: { metal: any; customerId: string }) => {
  const { label, verb } = getPassbookAction(
    metal.order?.paymentMethod,
    metal.order?.customerId,
    metal.type,
    customerId
  );

  return (
    <div className="txn-order-cell">
      {(metal.items || []).map((item: any, i: number) => {
        const bd = item?.itemBreakDown?.[0];
        const metalName = bd?.name ?? 'Metal';
        const qty = bd ? formatAmount(bd.qty, 4) : '0.0000';
        const discountRaw = metal?.order?.items?.[i]?.subItems?.[0]?.discount?.[0];
        const discountText = discountRaw?.issueDiscountType
          ? getDiscountText(
            discountRaw.issueDiscountType,
            discountRaw.value,
            discountRaw.secondaryMetal,
            metalName,
            discountRaw.cashbackType
          )
          : '';

        return (
          <div key={i} className="txn-order-line">
            {/* e.g.  Gold  ·  Bought  ·  0.0240g */}
            <span className="txn-metal-name">{metalName}</span>
            {/* <span className="txn-dot-sep">·</span> */}
            <span className={`txn-action-verb ${verb}`}>{label}</span>
            {/* <span className="txn-dot-sep">·</span> */}
            <span className="txn-qty">{qty}g</span>
            {discountText && (
              <>
                {/* <span className="txn-dot-sep">·</span> */}
                <span className="txn-discount-text"> +  {discountText}</span>
              </>
            )}
          </div>
        );
      })}
    </div>
  );
};


const MetalTransactions: React.FC<PropsType> = ({ metalData, type }) => {
  const [customer, user, customerId] = userStore((state: any) => [
    state.customer,
    state.user,
    state.customerId,
  ]);

  const [selectedMetalData, setSelectedMetalData] = useState<any>(null);
  const [isShow, setIsShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openBreakdown, setOpenBreakdown] = useState<string | null>(null);
  const breakdownRef = useRef<HTMLDivElement>(null);

  const items: any[] = metalData;

  const onHandleSideModal = (v: boolean) => setIsShow(v);

  const getOrderDetails = async (orderRef: string) => {
    try {
      setLoading(true);
      const res: any = await PassbookService.getOrderDetails(orderRef, user?.userId);
      if (res.status === 200 && res.data.data.result?.length > 0) {
        setSelectedMetalData(res.data.data.result[0]);
        onHandleSideModal(true);
      }
    } catch (e: any) {
      console.log(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (breakdownRef.current && !breakdownRef.current.contains(e.target as Node)) {
        setOpenBreakdown(null);
      }
    };
    if (openBreakdown) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openBreakdown]);


  const parentBuckets: Record<string, any[]> = {};
  items.forEach((row: any) => {
    if (row.isGrouped && row.mmParentOrderRef) {
      if (!parentBuckets[row.mmParentOrderRef]) parentBuckets[row.mmParentOrderRef] = [];
      parentBuckets[row.mmParentOrderRef].push(row);
    }
  });

  type DR =
    | { kind: 'parent'; parentRef: string; subOrders: any[] }
    | { kind: 'singleOrder'; row: any };

  const displayRows: DR[] = [];
  const emitted = new Set<string>();

  items.forEach((row: any) => {
    if (row.isGrouped && row.mmParentOrderRef) {
      if (!emitted.has(row.mmParentOrderRef)) {
        emitted.add(row.mmParentOrderRef);
        displayRows.push({
          kind: 'parent',
          parentRef: row.mmParentOrderRef,
          subOrders: parentBuckets[row.mmParentOrderRef],
        });
      }
    } else {
      displayRows.push({ kind: 'singleOrder', row });
    }
  });

  return (
    <>
      {selectedMetalData && (
        <OrderSummary
          handleModal={setIsShow}
          isShow={isShow}
          data={selectedMetalData}
          isPassbookRedirect={false}
          displaySummary={true}
          displayInvoice={
            customer?.customerCategory !== 'REGISTERED' ||
            selectedMetalData?.type !== 'BUYBACK'
          }
        />
      )}

      {loading && <Loader />}

      <div className="txn-wrapper">
        <div className="txn-scroll">
          <table className="txn-table">
            <colgroup>
              <col className="txn-col-orderid" />
              <col className="txn-col-type" />
              <col className="txn-col-order" />
              <col className="txn-col-date" />
              <col className="txn-col-status" />
              <col className="txn-col-amount" />
              <col className="txn-col-action" />
            </colgroup>
            <thead>
              <tr>
                <th>Order ID</th>
                <th>Type</th>
                <th>Order Details</th>
                <th>Date</th>
                <th>Status</th>
                <th className="right">Amount</th>
                <th className="center">Action</th>
              </tr>
            </thead>

            <tbody>
              {displayRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="txn-empty">
                    <div className="txn-empty-icon"></div>
                    <div className="txn-empty-text">No transactions found</div>
                  </td>
                </tr>
              )}

              {displayRows.map((dr, index) => {


                if (dr.kind === 'parent') {
                  const { parentRef, subOrders } = dr;
                  const firstSub = subOrders[0];
                  const totalAmount = subOrders.reduce(
                    (s: number, o: any) => s + (parseFloat(o.quotationTotal) || 0),
                    0
                  );
                  const allStatuses = Array.from(
                    new Set(subOrders.map((s: any) => s.status).filter(Boolean))
                  ) as string[];

                  const metalSummary: { name: string; totalQty: number }[] = [];
                  subOrders.forEach((sub: any) => {
                    (sub.items || []).forEach((item: any) => {
                      const bd = item?.itemBreakDown?.[0];
                      if (!bd) return;
                      const ex = metalSummary.find((m) => m.name === bd.name);
                      if (ex) ex.totalQty += parseFloat(bd.qty) || 0;
                      else metalSummary.push({ name: bd.name, totalQty: parseFloat(bd.qty) || 0 });
                    });
                  });

                  const isBreakdownOpen = openBreakdown === parentRef;

                  return (
                    <tr key={`parent-${parentRef}-${index}`} className="txn-row-parent">

                      {/* Order ID */}
                      <td>
                        <div className="txn-order-id-cell">
                          <span className="txn-parent-id">
                            <EbRef value={parentRef} />
                          </span>
                          <div className="txn-parent-meta">
                            {/* <span className="txn-sip-badge">SIP</span> */}
                            {/* <span className="txn-sub-count">{subOrders.length} orders</span> */}
                          </div>
                        </div>
                      </td>

                      {/* Type */}
                      <td><TypeBadge metal={firstSub} /></td>

                      {/* Order Details — plain metal summary + View Order Details toggle */}
                      <td>
                        <div className="txn-parent-order-cell">
                          {/* Plain text: "Gold · 0.0120g,  Silver · 0.0050g" */}
                          {/* <div className="txn-parent-metal-summary">
                            {metalSummary.map((m, i) => (
                              <span key={i} className="txn-parent-metal-item">
                                <span className="txn-metal-name">{m.name}</span>
                                <span className="txn-dot-sep">·</span>
                                <span className="txn-qty">{formatAmount(m.totalQty, 4)}g</span>
                                {i < metalSummary.length - 1 && <span className="txn-comma">,</span>}
                              </span>
                            ))}
                          </div> */}

                          {/* Breakdown toggle */}
                          <div
                            className="txn-split-wrap"
                            ref={isBreakdownOpen ? breakdownRef : undefined}
                          >
                            <button
                              className={`txn-split-btn${isBreakdownOpen ? ' active' : ''}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenBreakdown(isBreakdownOpen ? null : parentRef);
                              }}
                            >
                              View order details
                              <svg
                                className={`txn-split-chevron${isBreakdownOpen ? ' open' : ''}`}
                                width="10" height="10" viewBox="0 0 10 10" fill="none"
                              >
                                <path d="M2 3.5L5 6.5L8 3.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>

                            {isBreakdownOpen && (
                              <div className="txn-sip-dropdown">
                                <div className="txn-sip-dropdown-header">Order Breakdown</div>
                                {subOrders.map((sub: any, i: number) => {
                                  const bd = sub.items?.[0]?.itemBreakDown?.[0];
                                  if (!bd) return null;
                                  return (
                                    <div key={i} className="txn-sip-dropdown-row">
                                      {/* Metal name + grams */}
                                      <div className="txn-sip-left">
                                        <span className="txn-sip-metal">{bd.name}</span>
                                        <span className="txn-sip-gram">
                                          {formatAmount(bd.qty, 4)}g
                                          {bd.summary?.unitPrice && (
                                            <span className="txn-sip-rate">
                                              {' '}@ ₹{formatAmount(bd.summary.unitPrice)}/g
                                            </span>
                                          )}
                                        </span>
                                      </div>
                                      {/* Amount */}
                                      <span className="txn-sip-amount">
                                        ₹{formatAmount(sub.quotationTotal)}
                                      </span>
                                    </div>
                                  );
                                })}
                                <div className="txn-sip-dropdown-total">
                                  <span>Total</span>
                                  <span>₹{formatAmount(totalAmount)}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Date */}
                      <td>
                        <div className="txn-date">{moment(firstSub?.createdAtUnix).format('DD MMM YYYY')}</div>
                        <div className="txn-time">{moment(firstSub?.createdAtUnix).format('hh:mm A')}</div>
                      </td>

                      {/* Status */}
                      <td>
                        <div className="txn-status-stack">
                          {allStatuses?.map((s) => <StatusBadge key={s} status={s} />)}
                        </div>
                      </td>

                      {/* Amount */}
                      <td className="right">
                        <span className="txn-amount-parent">₹{formatAmount(totalAmount)}</span>
                      </td>

                      {/* View */}
                      <td className="center">
                        <button
                          className="txn-view-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            getOrderDetails(parentRef);
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M8 7V3M16 7V3M3 11H21M5 5H19C20.1 5 21 5.9 21 7V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V7C3 5.9 3.9 5 5 5Z"
                              stroke="currentColor" strokeWidth="2"
                              strokeLinecap="round" strokeLinejoin="round"
                            />
                          </svg>
                          View
                        </button>
                      </td>
                    </tr>
                  );
                }


                const metal = dr.row;
                return (
                  <tr key={`single-${metal.ebOrderRef}-${index}`} className="txn-row-single">
                    <td>
                      <span className="txn-order-id">
                        <EbRef value={metal.ebOrderRef} />
                      </span>
                    </td>
                    <td><TypeBadge metal={metal} /></td>
                    <td><OrderCell metal={metal} customerId={customerId} /></td>
                    <td>
                      <div className="txn-date">{moment(metal.createdAtUnix).format('DD MMM YYYY')}</div>
                      <div className="txn-time">{moment(metal.createdAtUnix).format('hh:mm A')}</div>
                    </td>
                    <td><StatusBadge status={metal.status} /></td>
                    <td className="right">
                      <span className="txn-amount-parent">₹{formatAmount(metal.quotationTotal)}</span>
                    </td>
                    <td className="center">
                      <button
                        className="txn-view-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          getOrderDetails(metal.ebOrderRef);
                        }}
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                          <path
                            d="M8 7V3M16 7V3M3 11H21M5 5H19C20.1 5 21 5.9 21 7V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V7C3 5.9 3.9 5 5 5Z"
                            stroke="currentColor" strokeWidth="2"
                            strokeLinecap="round" strokeLinejoin="round"
                          />
                        </svg>

                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default MetalTransactions;